import useIsEmea from "@dana/Common/Hooks/useIsEmea";
import WarningIcon from "@dana/Icons/WarningIcon";
import { themeColors, themeBackgrounds } from "@dana/MobiusOverrides/styles/colors";
import { fontSizes, lineHeights } from "@dana/MobiusOverrides/styles/typography";
import CartTotalDisplay, { CartTotalDisplayStyles } from "@dana/Overrides/Components/CartTotalDisplay";
import CartSaveOrderButton from "@dana/Overrides/Widgets/Cart/CartSaveOrderButton";
import { updateAnnualReturnsApprovalStatus } from "@dana/Services/AnnualReturnsService";
import { getDanaGeneralSettings } from "@dana/Store/Context/ContextSelectors";
import ApplicationStateDana from "@dana/Store/DanaApplicationState";
import { containsNonReturnableItems, exceedBalance, metMinimumOrderAmount } from "@dana/Store/Data/AnnualReturnsCart/AnnualReturnsCartSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { Cart } from "@insite/client-framework/Services/CartService";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import cancelPunchOut from "@insite/client-framework/Store/Context/Handlers/CancelPunchOut";
import {
    canCheckoutWithCart,
    canSubmitForQuote,
    canSubmitRequisition,
    getCurrentCartState,
    hasOnlyQuoteRequiredProducts,
    hasQuoteRequiredProducts,
    isCartCheckoutDisabled,
    isCartEmpty,
    isPunchOutOrder,
} from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import {
    getCurrentPromotionsDataView,
    getDiscountTotal,
    getOrderPromotions,
    getShippingPromotions,
} from "@insite/client-framework/Store/Data/Promotions/PromotionsSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import doPunchOutCheckout from "@insite/client-framework/Store/Pages/Cart/Handlers/DoPunchOutCheckout";
import submitRequisition from "@insite/client-framework/Store/Pages/Cart/Handlers/SubmitRequisition";
import preloadCheckoutShippingData from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/PreloadCheckoutShippingData";
import signInAsGuest from "@insite/client-framework/Store/Pages/SignIn/Handlers/SignInAsGuest";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import TwoButtonModal from "@insite/content-library/Components/TwoButtonModal";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import DataTable, { DataTableProps } from "@insite/mobius/DataTable";
import { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import { DataTableCellBaseProps } from "@insite/mobius/DataTable/DataTableCellBase";
import { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { DataTableBody, DataTableCell, DataTableHead, DataTableHeader, DataTableRow, Icon } from "@insite/mobius/index";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import { getCookie } from "@insite/mobius/utilities/cookies";
import get from "@insite/mobius/utilities/get";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    includeActions = "includeActions",
}

const mapStateToProps = (state: ApplicationStateDana) => {
    const promotionsDataView = getCurrentPromotionsDataView(state);
    const cartMode = getCookie('cartMode');
    let orderPromotions;
    let shippingPromotions;
    let discountTotal;
    if (promotionsDataView.value) {
        orderPromotions = getOrderPromotions(promotionsDataView.value);
        shippingPromotions = getShippingPromotions(promotionsDataView.value);
        discountTotal = getDiscountTotal(promotionsDataView.value);
    }

    const cartState = getCurrentCartState(state);
    const cart: Readonly<Cart> | undefined =
        cartMode === 'EmergencyOrderCart'
            ? (state.emergencyCartState.cart as Cart) ?? { properties: {} } as Cart
            : cartMode === 'StockOrderCart'
                ? cartState.value
                : (state.annualReturnsCartState.cart as Cart) ?? { properties: {} } as Cart;
    const isCartLoading = cartMode === 'StockOrderCart'
        ? cartState.isLoading
        : cartMode === 'EmergencyOrderCart'
            ? state.emergencyCartState.isLoading
            : state.annualReturnsCartState.isLoading;
    const { isUpdatingCart } = state.context;
    const {
        cartSettings: { onePageCheckout, showTaxAndShipping },
    } = getSettingsCollection(state);

    const isAnnualReturnsOrder = cartMode === 'AnnualReturnsOrderCart';
    const canBypassAnnualReturnsPolicy = state.context.session?.userRoles?.toLowerCase().indexOf("dana_annual_returns_admin") !== -1
        || cart?.properties?.annualReturnsApprovalStatus === "Approved";
    const metMinimumAnnualReturnsOrderAmount = isAnnualReturnsOrder && metMinimumOrderAmount(cart, getDanaGeneralSettings(state));
    const cartContainsNonReturnableItems = isAnnualReturnsOrder && containsNonReturnableItems(cart);
    const exceedAnnualReturnsBalance = exceedBalance(cart, state.annualReturnsOrderState?.balance ?? 0.00);
    const disableAnnualReturnsCheckout = isAnnualReturnsOrder && !canBypassAnnualReturnsPolicy && (!metMinimumAnnualReturnsOrderAmount || cartContainsNonReturnableItems || exceedAnnualReturnsBalance);

    return {
        isLoading: isCartLoading,
        cart,
        orderPromotions,
        shippingPromotions,
        discountTotal,
        showTaxAndShipping,
        checkoutShippingPageUrl: getPageLinkByPageType(state, "CheckoutShippingPage")?.url,
        checkoutReviewAndSubmitPageUrl: getPageLinkByPageType(state, "CheckoutReviewAndSubmitPage")?.url,

        isCartCheckoutDisabled:
            isCartLoading ||
            !cart ||
            isCartCheckoutDisabled(cart) ||
            isUpdatingCart ||
            state.pages.signIn.isSigningInAsGuest,
        isCartEmpty: isCartEmpty(cart),
        hasOnlyQuoteRequiredProducts: hasOnlyQuoteRequiredProducts(cart),
        hasQuoteRequiredProducts: hasQuoteRequiredProducts(cart),
        canSubmitForQuote: canSubmitForQuote(cart),
        rfqRequestQuotePageUrl: getPageLinkByPageType(state, "RfqRequestQuotePage")?.url,
        isPunchOutOrder: isPunchOutOrder(cart),
        canSubmitRequisition: canSubmitRequisition(cart),
        requisitionConfirmationPageUrl: getPageLinkByPageType(state, "RequisitionConfirmationPage")?.url,
        isPunchoutButtonDisabled: isCartLoading || isUpdatingCart,
        isSubmitForQuoteButtonDisabled: isCartLoading || isUpdatingCart,
        isSubmitRequisitionButtonDisabled: isCartLoading || isUpdatingCart,
        onePageCheckout,
        onePageCheckoutPageUrl: getPageLinkByPageType(state, "OnePageCheckoutPage")?.url,
        rememberMe: getSession(state).rememberMe,
        cartMode,
        isAnnualReturnsOrder,
        disableAnnualReturnsCheckout,
        isEmea: useIsEmea(state),
    };
};

const mapDispatchToProps = {
    preloadCheckoutShippingData,
    doPunchOutCheckout,
    cancelPunchOut,
    submitRequisition,
    signInAsGuest,
};

interface OwnProps extends WidgetProps {
    fields: {
        [fields.includeActions]: boolean;
    };
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasHistory & ResolveThunks<typeof mapDispatchToProps>;

export interface CartTotalStyles {
    cartTotal?: CartTotalDisplayStyles;
    checkoutButton?: ButtonPresentationProps;
    placeedToOrdercheckoutButton?: ButtonPresentationProps;
    cancelPunchOutButton?: ButtonPresentationProps;
    submitQuoteButton?: ButtonPresentationProps;
    continueShoppingButton?: ButtonPresentationProps;
    saveOrderButton?: ButtonPresentationProps;
    quoteRequiredModal?: ModalPresentationProps;
    submitRequisitionButton?: ButtonPresentationProps;
    confirmModal?: InjectableCss;
    message?: TypographyPresentationProps;
    shipToLocationName?: TypographyPresentationProps;
    goToCartButton?: ButtonPresentationProps;
    placeOrderButton?: ButtonPresentationProps;
    nonReturnableItemsModal?: ModalPresentationProps;
    nonReturnableItemsTable?: DataTableProps;
    nonReturnableItemsTableHead?: DataTableHeadProps;
    nonReturnableItemsTableHeader?: DataTableHeaderProps;
    nonReturnableItemsTableBody?: DataTableBodyProps;
    nonReturnableItemsTableRow?: DataTableRowProps;
    nonReturnableItemsTableCell?: DataTableCellBaseProps;
    rowError?: InjectableCss;
    errorMessage?: TypographyPresentationProps;
    confirmModalWrapper?: InjectableCss;
}

export const cartTotalStyles: CartTotalStyles = {
    checkoutButton: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            position: fixed;
                            left: 0;
                            bottom: 0;
                            width: 100%;
                            z-index: ${get(theme, "zIndex.stickyFooter")};
                        `,
                        css`
                            width: 100%;
                            margin-top: 10px;
                            position: inherit;
                            left: inherit;
                            bottom: inherit;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    placeedToOrdercheckoutButton: {
        css: css`
            width: 100%;
            margin-top: 10px;
        `,
    },
    confirmModalWrapper: {
        css: css`
            gap: 10px;
            display: flex;
            flex-direction: column;
        `,
    },
    cancelPunchOutButton: {
        buttonType: "outline",
        variant: "secondary",
        css: css`
            width: 100%;
            margin-top: 10px;
            position: inherit;
            left: inherit;
            bottom: inherit;
        `,
    },
    submitQuoteButton: {
        buttonType: "outline",
        variant: "secondary",
        css: css`
            width: 100%;
            margin-top: 10px;
            position: inherit;
            left: inherit;
            bottom: inherit;
        `,
    },
    continueShoppingButton: {
        buttonType: "outline",
        variant: "secondary",
        css: css`
            width: 100%;
            margin-top: 10px;
            position: inherit;
            left: inherit;
            bottom: inherit;
        `,
    },
    saveOrderButton: {
        buttonType: "outline",
        variant: "secondary",
        css: css`
            width: 100%;
            position: inherit;
            left: inherit;
            bottom: inherit;
        `,
    },
    submitRequisitionButton: {
        css: css`
            width: 100%;
            margin-top: 10px;
            position: inherit;
            left: inherit;
            bottom: inherit;
        `,
    },
    nonReturnableItemsModal: {
        size: 400,
        cssOverrides: {
            modalContent: css`
                padding: 0;
            `,
            modalTitle: css`
                padding: 10px 10px 10px 20px;
            `,
            headlineTypography: css`
                font-size: 20px;
                margin-bottom: 0;
            `,
        },
    },
    errorMessage: {
        size: fontSizes.xSmall,
        lineHeight: lineHeights.xSmall,
        color: themeColors.primary.richBlack,
        css: css`
                display: flex;
                align-items: center;
                gap: 4px 16px;
                padding: 10px;
                background: ${themeBackgrounds.offWhite};
                border: 1px solid ${themeBackgrounds.lightGray};
                flex: 1 1 100%;
                position: relative;
                flex-wrap: wrap;
                z-index: 1;
            `,
    },
};

const styles = cartTotalStyles;

const CartTotal = ({
    showTaxAndShipping,
    checkoutShippingPageUrl,
    checkoutReviewAndSubmitPageUrl,
    preloadCheckoutShippingData,
    history,
    isCartCheckoutDisabled,
    isCartEmpty,
    isLoading,
    cart,
    orderPromotions,
    shippingPromotions,
    discountTotal,
    hasOnlyQuoteRequiredProducts,
    hasQuoteRequiredProducts,
    canSubmitForQuote,
    rfqRequestQuotePageUrl,
    isPunchOutOrder,
    isPunchoutButtonDisabled,
    isSubmitForQuoteButtonDisabled,
    isSubmitRequisitionButtonDisabled,
    doPunchOutCheckout,
    cancelPunchOut,
    canSubmitRequisition,
    submitRequisition,
    requisitionConfirmationPageUrl,
    fields,
    onePageCheckout,
    onePageCheckoutPageUrl,
    signInAsGuest,
    rememberMe,
    cartMode,
    isAnnualReturnsOrder,
    disableAnnualReturnsCheckout,
    isEmea,
}: Props) => {
    const [quoteRequiredModalIsOpen, setQuoteRequiredModalIsOpen] = useState(false);
    const [openConfirmModal, SetOpenConfirmModal] = useState(false);
    const [canCheckout, setCanCheckout] = useState<boolean>(false);
    useEffect(() => {
        const canCheckoutCart = canCheckoutWithCart(cart);
        setCanCheckout(!!canCheckoutCart)
    }, [cart, cart?.cartLines, canCheckoutWithCart]);

    const toasterContext = useContext(ToasterContext);

    const checkoutHandler = () => {
        if (!cart) {
            return;
        }

        if (isPunchOutOrder) {
            doPunchOutCheckout();
            return;
        }

        if (!quoteRequiredModalIsOpen && hasQuoteRequiredProducts) {
            setQuoteRequiredModalIsOpen(true);
            return;
        }

        setQuoteRequiredModalIsOpen(false);

        const { isAuthenticated, isAwaitingApproval, id: cartId, canBypassCheckoutAddress } = cart;
        if (onePageCheckout && onePageCheckoutPageUrl) {
            if (!isAuthenticated && !rememberMe) {
                signInAsGuest({ returnUrl: onePageCheckoutPageUrl });
            } else {
                const updatedrl = cartMode === 'StockOrderCart'
                    ? onePageCheckoutPageUrl
                    : cartMode === 'EmergencyOrderCart'
                        ? `${onePageCheckoutPageUrl}?isEmergencyOrder=true`
                        : `${onePageCheckoutPageUrl}?isAnnualReturnsOrder=true`
                history.push(
                    isAwaitingApproval ? `${onePageCheckoutPageUrl}?cartId=${cartId}` : updatedrl,
                );
            }
            return;
        }

        if (canBypassCheckoutAddress) {
            history.push(checkoutReviewAndSubmitPageUrl!);
            return;
        }

        preloadCheckoutShippingData({
            cartId: isAwaitingApproval ? cartId : undefined,
            onSuccess: () => {
                history.push(
                    isAwaitingApproval ? `${checkoutShippingPageUrl}?cartId=${cartId}` : checkoutShippingPageUrl!,
                );
            },
        });
    };

    const cancelPunchOutHandler = () => {
        cancelPunchOut();
    };

    const submitForQuoteClickHandler = () => {
        if (rfqRequestQuotePageUrl) {
            history.push(rfqRequestQuotePageUrl);
        }
    };

    const continueShoppingHandler = () => {
        history.push('/');
    };

    const handleOpenConfirmModal = () => {
        SetOpenConfirmModal(true);
    };

    const handleCloseConfirmModal = () => {
        SetOpenConfirmModal(false);
    };
    const submitForQuoteLabel = cart?.isSalesperson ? translate("Create a Quote") : translate("Submit for Quote");

    const submitRequisitionClickHandler = () => {
        submitRequisition({
            onSuccess: ({ id }: any) => {
                if (requisitionConfirmationPageUrl) {
                    history.push(`${requisitionConfirmationPageUrl}?cartId=${id}`);
                }
            },
        });
    };

    const requestReviewClickHandler = async () => {
        if (!cart) { return; }

        const response = await updateAnnualReturnsApprovalStatus({
            cartId: cart?.id,
            approvalStatus: "Pending",
        });

        if (!response.successful) {
            toasterContext.addToast({
                body: translate("Failed to update annual returns approval status."),
                messageType: "danger",
            });
        } else {
            toasterContext.addToast({
                body: translate("Your request has been submitted successfully. Please check your inbox for more details."),
                messageType: "success",
            });
            history.push('/');
        }
    };

    const isZeroProductInCart = () => {
        if (isEmea === true) {
            if (cart?.orderGrandTotal === 0) {
                return true;
            }
            const id = (cart?.cartLines || [])
                .find(c => Number(c?.pricing?.extendedUnitNetPrice || 0) === 0)
                ?.productId ?? null;
            if (id !== null) {
                return true;
            }
            return false;
        }
        return false;
    };

    const nonreturnableItems = cart?.cartLines?.filter((p) => p.properties.isReturnable && p.properties.isReturnable.toLocaleLowerCase() !== 'true');
    if (isCartEmpty || cart?.cartLines?.length === 0) {
        return <></>;
    }

    return (
        <>
            <CartTotalDisplay
                showTaxAndShipping={showTaxAndShipping}
                extendedStyles={styles.cartTotal}
                isLoading={isLoading}
                isCartEmpty={isCartEmpty}
                cart={cart}
                orderPromotions={orderPromotions}
                shippingPromotions={shippingPromotions}
                discountTotal={discountTotal}
            />

            {fields.includeActions !== false && canCheckout && (
                <>
                    <Button
                        {...styles.checkoutButton}
                        onClick={checkoutHandler}
                        disabled={isZeroProductInCart() || isCartCheckoutDisabled || hasOnlyQuoteRequiredProducts || disableAnnualReturnsCheckout}
                        data-test-selector="cartTotal_checkout"
                    >
                        {cart?.requiresApproval ? translate("Checkout for Approval") : translate("Checkout")}
                    </Button>
                    <Modal
                        isOpen={openConfirmModal}
                        handleClose={handleCloseConfirmModal}
                        headline={translate("Confirmation")}
                    >
                        <StyledWrapper {...styles.confirmModal}>
                            <StyledWrapper className="cartConfirm" {...styles.confirmModalWrapper}>
                                <Typography {...styles.message}>
                                    {siteMessage("CartPage_OrderCheckoutMessage")}
                                </Typography>
                                {
                                    nonreturnableItems && nonreturnableItems.length > 0 &&
                                    <>
                                        <StyledWrapper {...styles.rowError}>
                                            <Typography {...styles.errorMessage}>
                                                <Icon src={WarningIcon} size={16} />
                                                <b>{translate("Please note: ")}</b>
                                                {translate("Some parts in your shopping cart are not returnable.")}
                                            </Typography>
                                        </StyledWrapper>
                                        <DataTable
                                            {...styles.nonReturnableItemsTable}
                                            data-test-selector="nonReturnableItemsModal"
                                        >
                                            <DataTableHead {...styles.nonReturnableItemsTableHead}>
                                                <DataTableHeader {...styles.nonReturnableItemsTableHeader}>
                                                    {translate("Part Number")}
                                                </DataTableHeader>
                                                <DataTableHeader {...styles.nonReturnableItemsTableHeader}>
                                                    {translate("Description")}
                                                </DataTableHeader>
                                                <DataTableHeader {...styles.nonReturnableItemsTableHeader}>
                                                    {translate("Quantity")}
                                                </DataTableHeader>
                                            </DataTableHead>
                                            <DataTableBody {...styles.nonReturnableItemsTableBody}>
                                                {nonreturnableItems?.map(item => (
                                                    <DataTableRow
                                                        {...styles.nonReturnableItemsTableRow}
                                                        key={item.id}
                                                    >
                                                        <DataTableCell
                                                            {...styles.nonReturnableItemsTableCell}
                                                            data-test-selector="nonReturnableItemsModal_PartNumber"
                                                        >
                                                            {item.erpNumber}
                                                        </DataTableCell>
                                                        <DataTableCell
                                                            {...styles.nonReturnableItemsTableCell}
                                                            data-test-selector="nonReturnableItemsModal_Description"
                                                        >
                                                            {item.shortDescription}
                                                        </DataTableCell>
                                                        <DataTableCell
                                                            {...styles.nonReturnableItemsTableCell}
                                                            data-test-selector="nonReturnableItemsModal_Quantity"
                                                        >
                                                            {item.qtyOrdered?.toString()}
                                                        </DataTableCell>
                                                    </DataTableRow>
                                                ))}
                                            </DataTableBody>
                                        </DataTable>
                                    </>
                                }
                                <Button
                                    {...styles.placeedToOrdercheckoutButton}
                                    onClick={checkoutHandler}
                                    disabled={isCartCheckoutDisabled || hasOnlyQuoteRequiredProducts}
                                >
                                    {cart?.requiresApproval ? translate("Checkout for Approval") : translate("Proceed to place order")}
                                </Button>
                                <Button
                                    {...styles.saveOrderButton}
                                    onClick={handleCloseConfirmModal}
                                    variant="secondary"
                                    className="button"
                                >
                                    {translate("Go to Cart")}
                                </Button>
                            </StyledWrapper>
                        </StyledWrapper>
                    </Modal>
                    {isPunchOutOrder && (
                        <Button
                            {...styles.cancelPunchOutButton}
                            disabled={isPunchoutButtonDisabled}
                            onClick={cancelPunchOutHandler}
                        >
                            {translate("Cancel PunchOut")}
                        </Button>
                    )}
                    <TwoButtonModal
                        headlineText=""
                        modalIsOpen={quoteRequiredModalIsOpen}
                        messageText={siteMessage("OrderApproval_RequiresQuoteMessage")}
                        cancelButtonText={translate("Cancel")}
                        submitButtonText={translate("Continue")}
                        onCancel={() => setQuoteRequiredModalIsOpen(false)}
                        onSubmit={checkoutHandler}
                        submitTestSelector="QuoteInCartMessageSubmit"
                        dataTestSelector="twoButtonModal_RequiresQuote"
                    ></TwoButtonModal>
                </>
            )}
            {fields.includeActions !== false && canSubmitForQuote && !isAnnualReturnsOrder && (
                <Button
                    {...styles.submitQuoteButton}
                    disabled={isSubmitForQuoteButtonDisabled}
                    onClick={submitForQuoteClickHandler}
                    data-test-selector="cartTotal_submitQuote"
                >
                    {submitForQuoteLabel}
                </Button>
            )}
            {fields.includeActions !== false && !isAnnualReturnsOrder && (
                <CartSaveOrderButton variant="button" extendedStyles={styles.saveOrderButton} />
            )}
            {fields.includeActions !== false && canSubmitRequisition && !isAnnualReturnsOrder && (
                <Button
                    {...styles.submitRequisitionButton}
                    disabled={isSubmitRequisitionButtonDisabled}
                    onClick={submitRequisitionClickHandler}
                    data-test-selector="cartTotal_submitRequisition"
                >
                    {translate("Submit Requisition")}
                </Button>
            )}
            {fields.includeActions !== false && isAnnualReturnsOrder && disableAnnualReturnsCheckout && (
                <Button
                    {...styles.submitRequisitionButton}
                    disabled={cart?.properties?.annualReturnsApprovalStatus === "Pending"}
                    onClick={requestReviewClickHandler}
                    data-test-selector="cartTotal_requestReview"
                >
                    {translate("Request Sales Operations Review")}
                </Button>
            )}
            <Button
                {...styles.continueShoppingButton}
                onClick={continueShoppingHandler}
                disabled={false}
                data-test-selector="cartTotal_continueShopping"
            >
                {translate("Continue Shopping")}
            </Button>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(CartTotal)),
    definition: {
        group: "Cart",
        icon: "cart-shopping",
        allowedContexts: ["CartPage"],
        fieldDefinitions: [
            {
                name: fields.includeActions,
                displayName: "Include Actions",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                isRequired: false,
                variant: "toggle",
                sortOrder: 0,
            }
        ],
    },
};

export default widgetModule;
